/**
 * SSE Service
 * Generic service for Server-Sent Events using @microsoft/fetch-event-source
 * This library properly handles POST-based SSE and works around browser limitations
 */

import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source';
import * as Sentry from '@sentry/react';
import { BASE_URL, SSE_SUBSCRIBE_URL } from '../../constants/endpoints';
import { getFirebaseIdToken, getFirebaseUserId } from '../../utils/firebaseAuth';
import type {
    SSESubscribeRequest,
    SSEEventHandlers,
    SSEConnection,
    SSEHeartbeatData,
    SSEConnectedData,
    SSEErrorData,
} from './types';

/**
 * Create an SSE connection using @microsoft/fetch-event-source
 * @param request - Subscription request with streamType and shopId
 * @param handlers - Event handlers for different SSE events
 * @returns SSEConnection object with close() method
 */
export function createSSEConnection<T>(
    request: SSESubscribeRequest,
    handlers: SSEEventHandlers<T>
): SSEConnection {
    const abortController = new AbortController();

    const connect = async () => {
        // Get auth token from Firebase SDK (waits for auth init, auto-refreshes if expired)
        const idToken = await getFirebaseIdToken();
        const userId = await getFirebaseUserId();
        
        if (!idToken || !userId) {
            Sentry.captureMessage('SSE connection failed: No auth token available', {
                level: 'warning',
                tags: { component: 'SSE Service' },
            });
            handlers.onError?.(new Error('No auth token available for SSE connection'));
            return;
        }

        const url = `${BASE_URL}${SSE_SUBSCRIBE_URL}`;
        Sentry.addBreadcrumb({
            category: 'sse',
            message: `Connecting to SSE: ${url}`,
            level: 'info',
            data: { request },
        });

        await fetchEventSource(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-auth-token': idToken,
                'firebase-user-id': userId,
            },
            body: JSON.stringify(request),
            signal: abortController.signal,
            openWhenHidden: true, // Keep connection open even when tab is hidden

            async onopen(response) {
                Sentry.addBreadcrumb({
                    category: 'sse',
                    message: `Connection opened, status: ${response.status}`,
                    level: 'info',
                    data: { contentType: response.headers.get('content-type') },
                });

                if (response.ok && response.headers.get('content-type')?.includes(EventStreamContentType)) {
                    Sentry.addBreadcrumb({
                        category: 'sse',
                        message: 'Successfully opened SSE stream',
                        level: 'info',
                    });
                    return; // Everything's good
                } else if (response.ok) {
                    // Response is OK but content-type is not event-stream
                    Sentry.addBreadcrumb({
                        category: 'sse',
                        message: `Response OK but content-type is: ${response.headers.get('content-type')}. Treating as SSE stream anyway.`,
                        level: 'warning',
                    });
                    return;
                } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
                    // Client-side errors (except rate limit) - don't retry
                    Sentry.captureMessage(`SSE client error: ${response.status}`, 'error');
                    throw new Error(`SSE client error: ${response.status}`);
                } else {
                    Sentry.captureMessage(`SSE server error or unexpected response: ${response.status}`, 'error');
                    throw new Error(`SSE server error: ${response.status}`);
                }
            },

            onmessage(msg) {
                Sentry.addBreadcrumb({
                    category: 'sse',
                    message: `Message received: ${msg.event || 'message'}`,
                    level: 'debug',
                    data: { eventId: msg.id },
                });

                // Parse the data
                let data: unknown;
                try {
                    data = JSON.parse(msg.data);
                } catch {
                    data = msg.data;
                }

                // Handle based on event type
                const eventType = msg.event || 'message';

                switch (eventType) {
                    case 'heartbeat':
                        Sentry.addBreadcrumb({
                            category: 'sse',
                            message: 'Heartbeat received',
                            level: 'debug',
                        });
                        handlers.onHeartbeat?.(data as SSEHeartbeatData);
                        break;

                    case 'connected':
                        Sentry.addBreadcrumb({
                            category: 'sse',
                            message: 'Connected event received',
                            level: 'info',
                        });
                        handlers.onConnected?.(data as SSEConnectedData);
                        break;

                    case 'error':
                        Sentry.captureMessage('SSE error event received', {
                            level: 'error',
                            extra: { data },
                        });
                        handlers.onError?.(data as SSEErrorData);
                        break;

                    default:
                        // For stream-type specific events (e.g., 'new-bills')
                        if (eventType === request.streamType) {
                            Sentry.addBreadcrumb({
                                category: 'sse',
                                message: `${eventType} data received`,
                                level: 'info',
                            });
                            handlers.onData(data as T);
                        } else {
                            Sentry.addBreadcrumb({
                                category: 'sse',
                                message: `Unknown event type: ${eventType}`,
                                level: 'warning',
                                data: { eventData: data },
                            });
                        }
                        break;
                }
            },

            onclose() {
                Sentry.addBreadcrumb({
                    category: 'sse',
                    message: 'Connection closed by server',
                    level: 'warning',
                });
                handlers.onError?.(new Error('SSE connection closed by server'));
            },

            onerror(err) {
                Sentry.captureException(err, {
                    tags: { component: 'SSE Service' },
                });
                handlers.onError?.(err);
                // Throw to prevent automatic retry (we handle retry in the hook)
                throw err;
            },
        });
    };

    // Start connection
    connect().catch((error) => {
        if (error.name !== 'AbortError') {
            Sentry.captureException(error, {
                tags: { component: 'SSE Service' },
                extra: { message: 'Failed to establish SSE connection' },
            });
        }
    });

    // Return control object
    return {
        close: () => {
            Sentry.addBreadcrumb({
                category: 'sse',
                message: 'Connection closed by client',
                level: 'info',
            });
            abortController.abort();
        },
    };
}
